import React from 'react'
import {
  ChevronDown,
  ChevronUp,
  Info,
  LoaderCircle,
  Search,
  X,
} from "lucide-react"

import { NETWORKS, type NetworkSlug } from "@/lib/networks"
import { HELP_TEXT, TAP_TARGET } from "@/lib/ui-tokens"
import { cn } from "@/lib/utils"
import {
  ConnectNewTile,
  OutputTile,
  type OutputAccount,
} from "@/shadcn/components/NewAutomation/OutputTile"
import { Button } from "@/shadcn/components/ui/button"
import { Input } from "@/shadcn/components/ui/input"
import { Networks } from "@/shadcn/components/ui/networks"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn/components/ui/select"
import { Separator } from "@/shadcn/components/ui/separator"
import { Switch } from "@/shadcn/components/ui/switch"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/components/ui/tooltip"

// DEV HANDOFF ─ "Connect social" dialog flow. Figma: S311lGXE6JSyTnNAXHmtR7 —
// network picker 29523-3529, account picker 29543-2146, credential form
// 29555-1888, Output Feed settings 29556-1903.
//
// FOUR FIGMA FRAMES, ONE COMPONENT. They are consecutive screens of the same
// dialog, so a single `screen` prop swaps the body while the card, header and
// footer chrome stay put:
//
//   "network"      Pick a network (6 primary + 14 under "More destinations")
//   "accounts"     Pick which connected account/page to post to (OAuth networks)
//   "credentials"  Enter service credentials (Bluesky, Mastodon, Discord, …)
//   "output-feed"  Configure the Output Feed being connected
//
// Which of the last three a network lands on is a PRODUCT decision made outside
// this file: OAuth networks return accounts, credential networks return a form,
// and the Feed output returns its settings. `output-feed` joined the flow when
// 29556-1903 was re-drawn with a Back / Connect footer — it was previously a
// standalone `output-feed-settings.tsx`, now folded in so the flow is one file.
//
// "credentials" and "output-feed" are the SAME renderer over different
// `FormField[]` data (see FormScreen), so a new credential network or a new
// setting is an array entry, not new code.
//
// Renders the DIALOG SURFACE only (not a portaling <Dialog>) so it stays
// self-contained and pixel-reviewable — same approach as welcome-modal.tsx and
// new-social-suggest-feeds.tsx. `ConnectSocialDialog.tsx` is the host that puts
// it in a Radix Dialog for prod.
//
// PORTAL PATCH — two things here portal: every help Tooltip, and the Select on
// the "output-feed" screen. Both already carry `className="tw-root"` below; keep
// them, and add the same to the DialogContent you wrap this in, or the scoped
// preflight is lost.
//
// TOOLTIP COPY comes from the "overlay" frames in the Figma file. A `hint` array
// renders as stacked lines — the overlays pair an instruction with "Format:" and
// "Example:" lines. `FormField.hintOpen` pins one open, for review only.
//
// WIRED, as of the SocialModal conversion. Every handler is OPTIONAL and the
// element stays inert without one, so the frames still render from the defaults
// alone: onClose, onBack, onPrimary, onSelectNetwork, onToggleMore,
// onSelectAccount, onConnectNew, onSearchChange, onSearchToggle, onFieldChange.
// The text inputs are uncontrolled until `onFieldChange` arrives, which is what
// keeps the preview showing its sample values.
//
// The account tiles are now the SHARED ones — `OutputTile` / `ConnectNewTile`
// from NewAutomation/OutputTile, which the wizard's Destinations step draws.
// The copies that used to live here could only show initials; prod needs the
// avatar image, the dimmed treatment for an account awaiting re-auth, the
// retweet badge and a disabled tile's reason, all of which that tile has. Their
// network badge therefore comes from the `dlvr-networks` registry rather than
// from `svg-outputs` — the same mark the wizard shows for the same account.
//
// The picker's marks now come from the registry too: `NETWORKS[slug].color`
// names the same `svg-outputs` disc the kit's own `NETWORK_ICON` did, and its
// labels match `NETWORK_LABEL` string for string, so both tables are gone. That
// table had also drifted — it named a 384KB Instagram disc the registry replaced
// with a 95KB one.
//
// STILL DUPLICATED with CreateAutomation.tsx, and still deliberately so:
//   · ConnectNetwork ≡ CreateAutomation's OutputNetwork (same 21 members)
//   · PRIMARY_NETWORKS / MORE_NETWORKS ≡ EMPTY_NETWORKS / MORE_DESTINATIONS.
//
// ONE DELIBERATE DIVERGENCE FROM THE FRAME: 29543-2146 draws the account search
// as an always-open full-width Input above the divider. It renders here as a
// collapsed magnifier that expands, below the divider, matching the Outputs
// toolbar on CreateAutomation's Destinations step, so both output pickers share
// one search affordance. `searchOpen` drives it and defaults to `Boolean(query)`.
//
// PRODUCT RULE ON SEARCH — it should only appear once the user has MORE THAN 10
// social outputs. Under that the whole list is already on screen, so a filter is
// noise. `showSearch` carries the rule; pass `showSearch={outputs.length > 10}`
// in prod. It defaults to `true` here so the kit still renders the frame as
// drawn (which shows search against two pages).
//
// FONT NOTE: the Figma "More destinations" button is set in Geist; this renders
// it in the theme font (Inter) per project rules — flag if Geist is required.

/* ──────────────────────────────────────────────────────────────── types ── */

/** Networks a profile can be connected to. Mirrors CreateAutomation's `OutputNetwork`. */
export type ConnectNetwork =
  | "blogger"
  | "bluesky"
  | "discord"
  | "facebook"
  | "google-business"
  | "instagram"
  | "linkedin"
  | "mastodon"
  | "nextdoor"
  | "pinterest"
  | "reddit"
  | "rss"
  | "slack"
  | "snapchat"
  | "stocktwits"
  | "threads"
  | "tiktok"
  | "tumblr"
  | "wordpress"
  | "x"
  | "youtube"

export type ConnectScreen =
  | "network"
  | "accounts"
  | "credentials"
  | "output-feed"

/**
 * One already-connected account/page on the "accounts" screen.
 *
 * The shared destination tile's account (`NewAutomation/OutputTile`) with its
 * network made optional, so an account that doesn't name one falls back to the
 * dialog's. That tile is what the wizard's Destinations step draws, and the
 * frames are the same tile — so this screen renders it rather than a second copy
 * that could only ever show initials. What comes with it is what prod needs:
 * a real avatar image, the inactive treatment for an account awaiting re-auth,
 * the retweet badge, and a `disabledReason` tooltip for a gated combination.
 */
export type ConnectAccount = Omit<OutputAccount, "network"> & {
  /** Badge network. Defaults to the dialog's `network`. */
  network?: NetworkSlug | null
}

/**
 * One mark in the picker grid, when the caller needs more than a slug.
 *
 * The kit's own arrays are plain slugs; prod passes objects, because the network
 * list it draws is the server's `socials.types` and each mark carries the
 * `data-*` that `ConnectMixin.connectService` reads off `currentTarget.dataset`,
 * plus the entitlement and availability states the legacy grid drew (a disabled
 * X while `twitterDisabled`, a service the plan doesn't include).
 */
export type ConnectNetworkItem = {
  /**
   * Any registry slug. `ConnectNetwork` names the 21 the design's own grid
   * draws, but the marks render from the registry, so a caller building the
   * grid from the server's service list isn't held to that list.
   */
  network: NetworkSlug
  /**
   * React key, where the slug isn't unique. Two service codes can share one
   * mark — a personal Instagram and an Instagram Professional both draw the
   * Instagram disc — so a caller listing both needs to say which is which.
   */
  id?: string | number
  /** Accessible name and tooltip. Defaults to the network's own label. */
  label?: string
  /** Dimmed and unclickable, with `disabledReason` as its tooltip. */
  disabled?: boolean
  disabledReason?: string
  /** `data-*` attributes for the button, e.g. `{serviceid: 3, direct: true}`. */
  data?: Record<string, string | number | boolean | undefined>
}

/** A network's display name, from the registry. */
function networkLabel(network: NetworkSlug): string {
  return NETWORKS[network]?.label ?? network
}

/** Either form the network arrays accept. */
export type ConnectNetworkEntry = ConnectNetwork | ConnectNetworkItem

function toNetworkItem(entry: ConnectNetworkEntry): ConnectNetworkItem {
  return typeof entry === "string" ? { network: entry } : entry
}

/** `{serviceid: 3}` -> `{"data-serviceid": "3"}`, dropping absent values. */
function dataAttributes(
  data?: ConnectNetworkItem["data"]
): Record<string, string> {
  const attributes: Record<string, string> = {}

  for (const [key, value] of Object.entries(data ?? {})) {
    if (value === undefined || value === null) continue
    attributes[`data-${key}`] = String(value)
  }

  return attributes
}

/**
 * One control on either form screen — "credentials" and "output-feed" are the
 * same renderer over different field data, which is why a new credential
 * network (or a new setting) is an array entry rather than new code.
 */
export type FormField = {
  id: string
  label: string
  /** Which control renders. Defaults to "text". */
  kind?: "text" | "password" | "select" | "switch"
  /** kind: "select" */
  options?: string[]
  /** kind: "text" | "password" | "select" */
  value?: string
  /** kind: "switch" */
  checked?: boolean
  placeholder?: string
  /** Renders the red `*` after the label. */
  required?: boolean
  /**
   * Tooltip copy behind the help affordance — omit and no icon renders.
   *
   * An ARRAY renders as stacked lines, which is what the drawn overlays need:
   * an instruction, then its "Format:" and "Example:" lines. A plain string is
   * the single-line shorthand.
   *
   * Every hint uses the SAME glyph (lucide Info), by decision. The credential
   * frames draw CircleHelp and only 29556-1903 draws Info; carrying both meant
   * two different marks for one affordance across four screens of one dialog,
   * so they are unified on Info.
   */
  hint?: string | string[]
  /**
   * Pins the tooltip open. REVIEW AFFORDANCE ONLY — a hover-only overlay can't
   * be seen in a screenshot or on touch, so a preview state can force one open.
   * Never set it in production.
   */
  hintOpen?: boolean
  /** Read-only, pre-filled by the backend (e.g. Bluesky's `bsky.social`). */
  disabled?: boolean
  /** Muted 12px line under the control. */
  help?: string
  /** Trailing link inside `help`, e.g. "Generate in Bluesky". */
  helpLink?: { label: string; href?: string }
  /** Marks the input invalid and prints `error` in place of `help`. */
  error?: string
}

/** @deprecated Renamed to `FormField` when the Output Feed screen joined. */
export type CredentialField = FormField

type ConnectSocialProps = {
  className?: string
  /** Which screen of the flow to render. */
  screen?: ConnectScreen
  /**
   * Replaces the screen body, keeping the card, its width, its gap, the header
   * and the footer. For a body prod already owns: the accounts screen's grid is
   * fed by `SocialSelection`, which holds the selection gating, the retweet tray
   * and the click rules the legacy list has always applied. `screen` still picks
   * the geometry and the default copy.
   *
   * Render `AccountsScreen` / `FormScreen` inside a fragment rather than a
   * wrapper element — the card is a flex column and its gap is what spaces the
   * regions apart, so an extra div collapses them into one.
   */
  children?: React.ReactNode
  /**
   * Where the brand SVGs are served from. Defaults to this kit's local copies;
   * point it at `https://static.dlvrit.com/dist/img` in production.
   */
  assetBaseUrl?: string
  /**
   * The network being connected — drives the default title and the tile badge an
   * account doesn't name itself.
   *
   * Any registry slug, not just a `ConnectNetwork`: the picker's 21 are output
   * networks, and this dialog also serves input sources (Etsy, Shopify, Vimeo)
   * that never appear in that grid.
   */
  network?: NetworkSlug

  /* header */
  title?: string
  /** Second line under the title. Omit for a single-line header. */
  subtitle?: string

  /* screen: "network" */
  primaryNetworks?: ConnectNetworkEntry[]
  moreNetworks?: ConnectNetworkEntry[]
  /** Whether the "More destinations" list is revealed. */
  moreOpen?: boolean
  moreLabel?: string

  /* screen: "accounts" */
  accounts?: ConnectAccount[]
  /**
   * Whether the search affordance renders at all.
   *
   * PRODUCT RULE — only show search once the user has **more than 10 social
   * outputs**. Below that the whole list is on screen already, so a filter is
   * noise. Pass `showSearch={outputs.length > 10}` in prod.
   *
   * Defaults to `true` rather than deriving from `accounts.length`, so the kit
   * still renders 29543-2146 as drawn (the frame shows search against two
   * pages). See the `showSearch={false}` preview state for the rule in action.
   */
  showSearch?: boolean
  searchPlaceholder?: string
  /**
   * Whether the search field is expanded. Defaults to `Boolean(query)` — a
   * query with a collapsed field is not a state that can exist, so it is
   * derived rather than passed twice.
   */
  searchOpen?: boolean
  /** Current search text. Empty renders the placeholder. */
  query?: string
  /** Leading "+ Connect New" tile. */
  showConnectNew?: boolean
  /** Skeleton tiles while the account list is being fetched. */
  loadingAccounts?: boolean
  /** Shown when `accounts` is empty and not loading. */
  emptyMessage?: string
  /** Label on the leading tile. Defaults to "Connect New". */
  connectNewLabel?: string

  /* screen: "credentials" */
  fields?: FormField[]
  /** Form-level failure, e.g. "That handle and password didn't match." */
  error?: string

  /* footer — "network" has none in the design */
  backLabel?: string
  primaryLabel?: string
  primaryDisabled?: boolean
  /** Spinner + disabled on the primary button. */
  loading?: boolean
  /** The design uses Outline here; Ghost is the quieter variant. */
  backVariant?: "outline" | "ghost"
  /**
   * A note above the footer buttons — prod's "New X (Twitter) accounts will
   * post as retweets" link.
   */
  footerStatus?: React.ReactNode
  /** Renders the footer even on the network screen, which is drawn without one. */
  showFooter?: boolean
  /** Hides the primary button, leaving Back on its own. */
  showPrimary?: boolean
  /** Hides Back, leaving the primary on its own. */
  showBack?: boolean

  /* handlers — every one optional, so the kit still renders inert */
  onClose?: () => void
  onBack?: () => void
  onPrimary?: () => void
  onSelectNetwork?: (
    network: NetworkSlug,
    event: React.MouseEvent<HTMLButtonElement>
  ) => void
  onToggleMore?: () => void
  onSelectAccount?: (account: OutputAccount) => void
  onConnectNew?: () => void
  onSearchChange?: (query: string) => void
  onSearchToggle?: (open: boolean) => void
  onFieldChange?: (id: string, value: string | boolean) => void
}

/* ───────────────────────────────────────────────────────── design data ── */

/** Top row of the picker — the six the design promotes. */
export const PRIMARY_NETWORKS: ConnectNetwork[] = [
  "facebook",
  "x",
  "instagram",
  "linkedin",
  "threads",
  "pinterest",
]

/** Revealed by "More destinations" — 7-up on desktop, two rows. */
export const MORE_NETWORKS: ConnectNetwork[] = [
  "tiktok",
  "youtube",
  "bluesky",
  "google-business",
  "snapchat",
  "reddit",
  "wordpress",
  "blogger",
  "tumblr",
  "mastodon",
  "discord",
  "stocktwits",
  "nextdoor",
  "slack",
]

/** Bluesky's three fields, exactly as drawn in 29555-1888. */
export const BLUESKY_FIELDS: FormField[] = [
  {
    id: "service",
    label: "Service",
    placeholder: "bsky.social",
    disabled: true,
  },
  {
    id: "handle",
    label: "Bluesky Handle",
    placeholder: "yourhandle.bsky.social",
    hint: [
      'Log into Bluesky and go to Settings. Your handle is shown under "Signed in as."',
      "Format: yourhandle.bsky.social",
    ],
  },
  {
    id: "password",
    label: "Bluesky App Password",
    kind: "password",
    placeholder: "App password",
    hint: [
      "In Bluesky, go to Settings → App Passwords → Add App Password.",
      "Copy the generated password and paste it here.",
    ],
    help: "You'll need to generate a unique password for dlvr.it. ",
    helpLink: { label: "Generate in Bluesky" },
  },
]

/** The Output Feed screen's four controls, exactly as drawn in 29556-1903. */
export const OUTPUT_FEED_FIELDS: FormField[] = [
  {
    id: "output-feed-name",
    label: "Output Feed Name",
    placeholder: "e.g. My Instagram Feed",
    required: true,
  },
  {
    id: "output-feed-max-items",
    label: "Max Items in Output Feed",
    kind: "select",
    value: "10",
    options: ["5", "10", "15", "20", "25", "50"],
  },
  {
    id: "output-feed-static-title",
    label: "Static Title for Items",
    placeholder: "e.g. Shop our latest arrivals",
    hint: "Used as the title for every item posted to this feed.",
  },
  {
    id: "output-feed-active",
    label: "Output Feed Active",
    kind: "switch",
    checked: true,
  },
]

const DEFAULT_ACCOUNTS: ConnectAccount[] = [
  { id: "1", name: "Coffee Co", initials: "CC", subtype: "Page" },
  { id: "2", name: "dlvr.it Demo Page", initials: "DI", subtype: "Page" },
]

/**
 * Per-screen card geometry, straight from the frames: the picker is the widest
 * because its 7-up row needs the room, the credential form the narrowest.
 *
 * `accounts` is `max-w-lg` because `--container-lg` is exactly 32rem / 512px —
 * the drawn width, so it costs nothing to name it. The other two are literal on
 * purpose: 620px falls between `--container-xl` (576) and `--container-2xl`
 * (672), and 440px is 8px under `--container-md` (448). Snapping either to the
 * nearest step would move the design rather than tidy it, so the widths stay
 * exact and stay centralised HERE — this map is the one place to change them.
 */
const SCREEN_WIDTH: Record<ConnectScreen, string> = {
  network: "tw:max-w-[620px]",
  accounts: "tw:max-w-lg",
  credentials: "tw:max-w-[440px]",
  "output-feed": "tw:max-w-[440px]",
}

/**
 * The search row's own spacing. `-mt-3` halves the card's 24px gap above it,
 * matching the 12px the tile grid takes below — so the row reads as part of the
 * body it filters rather than as a band of its own under the header rule.
 * Applied to both the collapsed and expanded branches so opening search doesn't
 * shift anything.
 */
const SEARCH_ROW = "tw:-mt-3 tw:flex tw:w-full tw:items-center"

/** Card gap. The credential form breathes one step wider between fields. */
const SCREEN_GAP: Record<ConnectScreen, string> = {
  network: "tw:gap-6",
  accounts: "tw:gap-6",
  credentials: "tw:gap-7",
  "output-feed": "tw:gap-6",
}

/* ───────────────────────────────────────────────────────────── primitives ── */

/**
 * A brand mark from the network registry's `color` variant — the circular
 * brand disc, which is the artwork the picker frames draw.
 *
 * The kit used to carry its own `NETWORK_ICON` table of the same 21 files. It
 * was not just duplicated but drifting: it named
 * `instagram-dlvrit-social-output-output.svg`, a 384KB copy the registry had
 * already replaced with a 95KB one.
 */
function NetworkMark({
  network,
  base,
  className,
  size = "tw:size-6",
}: {
  network: NetworkSlug
  base: string
  className?: string
  size?: string
}) {
  return (
    <span
      className={cn(
        "tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden",
        /*
         * Nudged down-right to centre the MARK, not its file. The svg-outputs
         * badges bake in a drop shadow — `feOffset dx="4" dy="4"` — and pull the
         * disc up-left by that much so the shadow still fits the box: facebook
         * draws its disc at cx 56.73 cy 56.48 in a 121 box whose centre is 60.5,
         * and the rest of the set sits within half a unit of that. Centring the
         * file therefore leaves the visible disc high and left, which is what
         * made the hover fill look off-register around it. 4/121 = 3.3%, as a
         * percentage so it holds at both `size-14` and `sm:size-16`.
         *
         * On this span rather than the image inside it: the span clips, and
         * shifting the image within it would cut the shadow off. The button's
         * p-1.5 leaves room for the span itself to move.
         */
        "tw:translate-x-[3.3%] tw:translate-y-[3.3%]",
        size,
        className
      )}
    >
      <Networks
        name={network}
        variant="color"
        assetBaseUrl={base}
        className="tw:size-full"
      />
    </span>
  )
}

/**
 * A mark in the picker grid. The design draws bare marks, but picking one IS
 * the only action on that screen, so each gets a real focusable control with
 * the network name as its accessible name.
 *
 * The `data-*` are not decoration: prod's connect handler reads the service id,
 * subtype and direct-connect flag off `event.currentTarget.dataset`, so they
 * have to reach the DOM node the click lands on.
 */
function NetworkButton({
  item,
  base,
  size,
  onSelect,
}: {
  item: ConnectNetworkItem
  base: string
  size: string
  onSelect?: (
    network: NetworkSlug,
    event: React.MouseEvent<HTMLButtonElement>
  ) => void
}) {
  const label = item.label ?? networkLabel(item.network)

  return (
    <button
      type="button"
      aria-label={label}
      title={item.disabled ? item.disabledReason || label : label}
      disabled={item.disabled}
      onClick={
        onSelect ? (event) => onSelect(item.network, event) : undefined
      }
      {...dataAttributes(item.data)}
      /* inline-flex is load-bearing, not cosmetic: a <button> defaults to
         inline-block, and NetworkMark's span is inline-flex — an inline-level
         box. That made the button establish a line box, so the font's descender
         added ~6px under the baseline-aligned mark and the p-1.5 box came out
         76x82. `rounded-full` on a non-square box is an ELLIPSE, which is what
         the hover fill looked like. Making the button a flex container drops the
         strut, so the box is 76x76 and the hover is a true circle. */
      className={cn(
        "tw:inline-flex tw:items-center tw:justify-center tw:rounded-full tw:p-1.5 tw:transition-colors tw:outline-none tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50",
        item.disabled
          ? "tw:cursor-not-allowed tw:opacity-40"
          : "tw:hover:bg-muted"
      )}
    >
      <NetworkMark network={item.network} base={base} size={size} />
    </button>
  )
}

/** One placeholder tile — same footprint as OutputTile so nothing reflows. */
function AccountTileSkeleton() {
  return (
    <div
      className="tw:flex tw:min-w-0 tw:animate-pulse tw:flex-col tw:items-center tw:gap-2"
      aria-hidden
    >
      <span className="tw:size-12 tw:rounded-full tw:bg-muted" />
      <span className="tw:h-4 tw:w-4/5 tw:rounded-sm tw:bg-muted" />
      <span className="tw:h-3 tw:w-1/2 tw:rounded-sm tw:bg-muted" />
    </div>
  )
}

/* ──────────────────────────────────────────────────────────────── shell ── */

/**
 * Title centred against the close button by a same-size spacer, per the Figma
 * "Spacer (balances close button)" node — not by `text-center` alone, which
 * would drift by half the button as soon as the title wraps.
 *
 * The SUBTITLE sits OUTSIDE that row, spanning the full card width. Figma keeps
 * both in one 376px Text frame, but only the title needs balancing against the
 * close button — trapping the subtitle there too cost it 88px (28px spacer +
 * 28px button + two 16px gaps) and wrapped it early. Row heights are unchanged:
 * 28px title + 8px gap + 20px subtitle still totals the drawn 56px.
 */
export function ConnectDialogHeader({
  title,
  subtitle,
  onClose,
}: {
  title: string
  subtitle?: string
  onClose?: () => void
}) {
  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:gap-2">
      <div className="tw:flex tw:w-full tw:items-center tw:gap-4">
        <span aria-hidden className="tw:size-7 tw:shrink-0" />
        <h2
          className={cn(
            "tw:min-w-0 tw:flex-1 tw:text-center tw:text-lg tw:font-semibold tw:text-foreground",
            subtitle ? "tw:leading-7" : "tw:leading-6"
          )}
        >
          {title}
        </h2>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Close"
          onClick={onClose}
          className="tw:shrink-0 tw:text-muted-foreground"
        >
          <X className="tw:size-4" />
        </Button>
      </div>
      {subtitle ? (
        <p className="tw:w-full tw:text-center tw:text-sm tw:text-muted-foreground">
          {subtitle}
        </p>
      ) : null}
    </div>
  )
}

export function ConnectDialogFooter({
  backLabel,
  backVariant,
  primaryLabel,
  primaryDisabled,
  loading,
  status,
  showBack = true,
  showPrimary = true,
  onBack,
  onPrimary,
}: {
  backLabel: string
  backVariant: "outline" | "ghost"
  primaryLabel: string
  primaryDisabled?: boolean
  loading?: boolean
  /**
   * Back on its own. Not a state any frame draws — every drawn footer has both
   * buttons — but a screen whose choice IS the action has nothing for a primary
   * button to confirm, and still needs its way back.
   */
  showPrimary?: boolean
  /**
   * Primary on its own, right-aligned. For a screen that is the first of its
   * flow: the importer opens straight onto the account list, so there is no
   * previous screen for Back to mean.
   */
  showBack?: boolean
  /**
   * A note above the buttons — prod's "New X (Twitter) accounts will post as
   * retweets" link. The drawn row is back-left / primary-right with nothing
   * between them, so a third element goes above rather than inside it.
   */
  status?: React.ReactNode
  onBack?: () => void
  onPrimary?: () => void
}) {
  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:gap-3">
      {status ? (
        <p className={cn(HELP_TEXT, "tw:w-full tw:text-center")}>{status}</p>
      ) : null}
      {/* justify-end rather than between, so a lone primary still sits right —
          `between` would centre it once Back is gone. */}
      <div
        className={cn(
          "tw:flex tw:w-full tw:items-center tw:gap-3",
          showBack ? "tw:justify-between" : "tw:justify-end"
        )}
      >
        {showBack ? (
          <Button
            variant={backVariant}
            onClick={onBack}
            className={TAP_TARGET}
          >
            {backLabel}
          </Button>
        ) : null}
        {showPrimary ? (
          <Button
            onClick={onPrimary}
            className={TAP_TARGET}
            disabled={primaryDisabled || loading}
          >
            {loading ? (
              <LoaderCircle className="tw:size-4 tw:animate-spin" />
            ) : null}
            {primaryLabel}
          </Button>
        ) : null}
      </div>
    </div>
  )
}

/* ────────────────────────────────────────────────────────────── screens ── */

export function NetworkScreen({
  base,
  primary,
  more,
  moreOpen,
  moreLabel,
  heading,
  description,
  onSelectNetwork,
  onToggleMore,
}: {
  base: string
  primary: ConnectNetworkEntry[]
  more: ConnectNetworkEntry[]
  moreOpen: boolean
  moreLabel: string
  /**
   * A heading over the grid, for the screen rendered OUTSIDE this dialog: the
   * wizard's destinations step shows this same grid as its empty state, and
   * there is no card header there to name it. The dialog leaves both unset,
   * since its own header already does.
   */
  heading?: string
  description?: string
  onSelectNetwork?: (
    network: NetworkSlug,
    event: React.MouseEvent<HTMLButtonElement>
  ) => void
  onToggleMore?: () => void
}) {
  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:items-center tw:gap-6">
      {heading || description ? (
        /* Same scale as the dialog's own header, so the grid reads the same
           whichever of the two carries its title. */
        <div className="tw:flex tw:w-full tw:flex-col tw:gap-2 tw:text-center">
          {heading ? (
            <h2 className="tw:text-lg tw:leading-7 tw:font-semibold tw:text-foreground">
              {heading}
            </h2>
          ) : null}
          {description ? (
            <p className="tw:text-sm tw:text-muted-foreground">{description}</p>
          ) : null}
        </div>
      ) : null}

      {/* Two layouts, because the two widths want different things.

          Below `sm`: a wrapping flex row. Six 64px marks and their gaps overflow
          a phone, so they wrap — and a grid cannot centre the leftovers, it
          parks them in the first columns of the last row. flex-wrap +
          justify-center fills as many as fit and centres the remainder.

          At `sm`: a 6-column grid pinned to 572px — the width of the "more" grid
          below (seven 68px marks, six 16px gaps), so the two blocks line up as
          one column rather than two ragged ones. Six marks over the same span
          just sit further apart. Both counts are fixed by PRIMARY_NETWORKS and
          the 6/7 column classes, so 572 only moves if those do — change it with
          them.

          Not `w-full` at `sm`: a fixed column count divides whatever width the
          parent offers, and this renders in two very different ones — the
          dialog, and the wizard's destinations step (see `heading` above). At
          the wizard's ~1712px that spread the marks 212px apart. */}
      <div className="tw:flex tw:w-full tw:flex-wrap tw:justify-center tw:gap-4 tw:sm:grid tw:sm:w-[572px] tw:sm:grid-cols-6 tw:sm:justify-items-center">
        {primary.map(toNetworkItem).map((item) => (
          <NetworkButton
            key={item.id ?? item.network}
            item={item}
            base={base}
            size="tw:size-14 tw:sm:size-16"
            onSelect={onSelectNetwork}
          />
        ))}
      </div>

      {/* Nothing to disclose, no disclosure: prod builds these lists from the
          server's service list, which can be short enough to fit the top row. */}
      {more.length ? (
        <Button
          variant="ghost"
          /* aria-expanded stays — it's the disclosure's accessible state — but
             the ghost variant paints `aria-expanded:bg-muted`, which left the
             trigger sitting in a permanent grey pill whenever the list was open.
             Cleared here, with hover-while-open restored: `aria-expanded:hover:`
             is a two-variant selector, so it beats the single-variant reset
             regardless of the order Tailwind emits them in. */
          className={cn(
            TAP_TARGET,
            "tw:aria-expanded:bg-transparent tw:aria-expanded:hover:bg-muted"
          )}
          aria-expanded={moreOpen}
          onClick={onToggleMore}
        >
          {moreLabel}
          {moreOpen ? (
            <ChevronUp className="tw:size-4" />
          ) : (
            <ChevronDown className="tw:size-4" />
          )}
        </Button>
      ) : null}

      {moreOpen ? (
        /* w-fit for the same reason as the primary grid above. */
        <div className="tw:grid tw:w-fit tw:grid-cols-4 tw:justify-items-center tw:gap-4 tw:sm:grid-cols-7">
          {more.map(toNetworkItem).map((item) => (
            <NetworkButton
              key={item.id ?? item.network}
              item={item}
              base={base}
              size="tw:size-14"
              onSelect={onSelectNetwork}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function AccountsScreen({
  network,
  accounts,
  query,
  showSearch,
  searchOpen,
  searchPlaceholder,
  showConnectNew,
  connectNewLabel,
  loadingAccounts,
  emptyMessage,
  onSelectAccount,
  onConnectNew,
  onSearchChange,
  onSearchToggle,
}: {
  network: NetworkSlug
  accounts: ConnectAccount[]
  query?: string
  showSearch: boolean
  searchOpen: boolean
  searchPlaceholder: string
  showConnectNew: boolean
  connectNewLabel?: string
  loadingAccounts?: boolean
  emptyMessage: string
  onSelectAccount?: (account: OutputAccount) => void
  onConnectNew?: () => void
  onSearchChange?: (query: string) => void
  /** Opens and closes the collapsed magnifier. */
  onSearchToggle?: (open: boolean) => void
}) {
  const isEmpty = !loadingAccounts && accounts.length === 0

  return (
    <>
      {/* The divider is a HEADER RULE: it closes the header, and the search
          row then leads the body it filters. 29543-2146 puts the search above
          the divider, but a collapsed magnifier floating there read as
          detached from the tiles it acts on. */}
      <Separator className="tw:bg-border" />

      {/* Search is a COLLAPSED magnifier that expands, matching the Outputs
          toolbar on CreateAutomation's Destinations step — same ghost icon
          button, same size-11/md:size-8 target, same right alignment — rather
          than the always-open full-width Input drawn in 29543-2146. Deliberate
          divergence from the frame, requested so the two output pickers share
          one search affordance.

          NOTE — search should only appear once the user has MORE THAN 10 social
          outputs; under that the full list is already on screen and a filter is
          noise. `showSearch` carries that rule: pass
          `showSearch={outputs.length > 10}` in prod. */}
      {!showSearch ? null : searchOpen ? (
        <div className={cn(SEARCH_ROW, "tw:gap-1")}>
          <div className="tw:relative tw:min-w-0 tw:flex-1">
            {/* Leading glyph inside the field, as on the find step and the
                configure search card — so an expanded search still reads as
                search once the magnifier button is gone. */}
            <Search className="tw:pointer-events-none tw:absolute tw:top-1/2 tw:left-2.5 tw:size-4 tw:-translate-y-1/2 tw:text-muted-foreground" />
            <Input
              autoFocus={Boolean(onSearchToggle)}
              value={query ?? ""}
              onChange={
                onSearchChange
                  ? (event) => onSearchChange(event.target.value)
                  : undefined
              }
              readOnly={!onSearchChange}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className={cn(TAP_TARGET, "tw:pl-8")}
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="tw:size-11 tw:shrink-0 tw:md:size-8"
            aria-label="Close search"
            onClick={() => {
              // Closing search clears the filter with it: a collapsed field
              // showing a filtered list gives no way back to the full one.
              onSearchChange?.("")
              onSearchToggle?.(false)
            }}
          >
            <X />
          </Button>
        </div>
      ) : (
        <div className={cn(SEARCH_ROW, "tw:justify-end")}>
          {/* size-11, not TAP_TARGET: an icon button needs both dimensions, and
              `size="icon"` is a plain `size-8` in the cva, so tailwind-merge
              resolves it without an important. Collapsed and expanded are the
              same height, so opening search doesn't shift the tiles. */}
          <Button
            variant="ghost"
            size="icon"
            className="tw:size-11 tw:md:size-8"
            aria-label={searchPlaceholder}
            onClick={onSearchToggle ? () => onSearchToggle(true) : undefined}
          >
            <Search />
          </Button>
        </div>
      )}

      {/* Scrolls at two rows, as drawn. Taller below `sm`, where names wrap to
          two lines and the drawn 220px cut through the middle of row two.

          py-1 is load-bearing, NOT spacing: a tile's selection ring sits at
          -inset-0.5 and its check badge at -top-[3px], so both paint outside the
          tile's own box. A scroll container clips at its PADDING box, so with the
          grid flush against the top edge the first row lost the top of its ring
          and the badge's white border — a flat-topped avatar. 4px of padding is
          more than the 3px of outset, so the overflow now lands inside the clip.
          The max-heights carry the same +8px so the drawn 220px of *content*
          still shows. (CreateAutomation's copy of this grid doesn't scroll, which
          is why the same tile is fine there.) */}
      <div
        className={cn(
          "tw:max-h-[308px] tw:w-full tw:overflow-x-clip tw:overflow-y-auto tw:py-1 tw:sm:max-h-[228px]",
          // Only tighten to the search row when there is one — with search
          // hidden the grid sits under the header rule and keeps the card's
          // full 24px gap.
          showSearch && "tw:-mt-3"
        )}
      >
        <div className="tw:grid tw:grid-cols-3 tw:items-start tw:gap-4">
          {showConnectNew ? (
            <ConnectNewTile label={connectNewLabel} onSelect={onConnectNew} />
          ) : null}
          {loadingAccounts
            ? Array.from({ length: 5 }).map((_, i) => (
                <AccountTileSkeleton key={i} />
              ))
            : accounts.map((account) => (
                <OutputTile
                  key={account.id}
                  account={{
                    ...account,
                    /*
                     * `undefined` means "didn't say", so the dialog's network
                     * stands in. An explicit `null` means "this account has no
                     * mark" — `??` would collapse the two and badge an unmarked
                     * account with whatever the dialog happened to be about.
                     */
                    network:
                      account.network === undefined ? network : account.network,
                  }}
                  onSelect={onSelectAccount}
                />
              ))}
        </div>

        {isEmpty ? (
          /* text-sm, not HELP_TEXT: empty-state copy stays readable at the
             moment the user most needs it. */
          <p className="tw:mt-6 tw:text-center tw:text-sm tw:text-muted-foreground">
            {emptyMessage}
          </p>
        ) : null}
      </div>
    </>
  )
}

/**
 * Label row: text, then the red `*` at 4px, then the help glyph at 6px. Margins
 * rather than flex gap, because the two trailing marks sit at different offsets
 * and can appear on the same row.
 */
function FieldLabel({ field }: { field: FormField }) {
  return (
    <div className="tw:flex tw:min-w-0 tw:items-center">
      <label
        htmlFor={field.id}
        className="tw:text-sm tw:font-medium tw:text-foreground"
      >
        {field.label}
      </label>
      {field.required ? (
        <span
          aria-hidden
          className="tw:ml-1 tw:text-sm tw:font-medium tw:text-destructive"
        >
          *
        </span>
      ) : null}
      {field.hint ? (
        <Tooltip open={field.hintOpen ? true : undefined}>
          <TooltipTrigger asChild>
            {/* aria-label names the BUTTON; the tooltip is its description, so
                the hint isn't announced twice. No `title` — a native tooltip
                would double up with the real one. */}
            <button
              type="button"
              aria-label={`More about ${field.label}`}
              className="tw:ml-1.5 tw:rounded-xs tw:text-muted-foreground tw:outline-none tw:hover:text-foreground tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50"
            >
              <Info className="tw:size-3.5" />
            </button>
          </TooltipTrigger>
          {/* Portal patch: TooltipContent renders through a Radix Portal, so it
              lands outside `.tw-root` and loses the scoped preflight without
              this. flex-col/items-start because the primitive centres a single
              inline row — multi-line overlay copy has to stack left-aligned. */}
          <TooltipContent
            className={cn(
              "tw-root",
              Array.isArray(field.hint) && "tw:flex-col tw:items-start"
            )}
          >
            {Array.isArray(field.hint)
              ? field.hint.map((line) => <span key={line}>{line}</span>)
              : field.hint}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  )
}

/**
 * The body shared by the "credentials" and "output-feed" screens: both are the
 * same stack of label + control + help, so they are one renderer over different
 * `FormField[]` data rather than two near-identical subtrees.
 */
export function FormScreen({
  fields,
  error,
  onFieldChange,
  onSubmit,
}: {
  fields: FormField[]
  error?: string
  /**
   * A control changed. `value` is the text for a text/password/select field and
   * the boolean for a switch, so one callback covers every field kind and the
   * caller keys off the id it supplied.
   */
  onFieldChange?: (id: string, value: string | boolean) => void
  /** Enter in a text field. Prod submits the credential form that way. */
  onSubmit?: () => void
}) {
  return (
    <>
      {error ? (
        <p
          role="alert"
          className="tw:w-full tw:rounded-lg tw:bg-destructive/10 tw:px-3 tw:py-2 tw:text-sm tw:text-destructive"
        >
          {error}
        </p>
      ) : null}

      {fields.map((field) =>
        /* A switch is a ROW, not a stacked field: label left, control right —
           the only field shape in either frame that isn't label-over-control. */
        field.kind === "switch" ? (
          <div
            key={field.id}
            className="tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-4"
          >
            <FieldLabel field={field} />
            <Switch
              id={field.id}
              checked={field.checked}
              onCheckedChange={
                onFieldChange
                  ? (checked) => onFieldChange(field.id, checked)
                  : undefined
              }
            />
          </div>
        ) : (
          <div
            key={field.id}
            className="tw:flex tw:w-full tw:flex-col tw:gap-2"
          >
            <FieldLabel field={field} />

            {field.kind === "select" ? (
              /* h-11! / md:h-8! rather than TAP_TARGET: SelectTrigger sizes
                 itself with `data-[size=default]:h-8`, a class+attribute
                 selector at specificity (0,2,0), so a plain h-11 (0,1,0) loses
                 to it whatever the source order. w-full needs no `!` — it and
                 the primitive's w-fit are one tailwind-merge group. */
              <Select
                value={field.value}
                onValueChange={
                  onFieldChange
                    ? (value) => onFieldChange(field.id, value)
                    : undefined
                }
              >
                <SelectTrigger
                  id={field.id}
                  className="tw:h-11! tw:w-full tw:md:h-8!"
                >
                  <SelectValue placeholder={field.placeholder} />
                </SelectTrigger>
                {/* Portal patch: content renders outside `.tw-root`. */}
                <SelectContent className="tw-root">
                  {(field.options ?? []).map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id={field.id}
                type={field.kind === "password" ? "password" : "text"}
                /* Controlled once a handler is passed; uncontrolled without one,
                   so the kit's own preview still shows its sample values. */
                {...(onFieldChange
                  ? {
                      value: field.value ?? "",
                      onChange: (
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => onFieldChange(field.id, event.target.value),
                    }
                  : { readOnly: true, defaultValue: field.value })}
                onKeyDown={
                  onSubmit
                    ? (event) => {
                        if (event.key === "Enter") {
                          event.preventDefault()
                          onSubmit()
                        }
                      }
                    : undefined
                }
                placeholder={field.placeholder}
                disabled={field.disabled}
                autoComplete={
                  field.kind === "password" ? "new-password" : undefined
                }
                aria-invalid={field.error ? true : undefined}
                aria-describedby={field.error ? `${field.id}-error` : undefined}
                className={TAP_TARGET}
              />
            )}

            {field.error ? (
              <p
                id={`${field.id}-error`}
                className="tw:text-xs tw:text-destructive"
              >
                {field.error}
              </p>
            ) : field.help ? (
              <p className={HELP_TEXT}>
                {field.help}
                {field.helpLink ? (
                  <a
                    href={field.helpLink.href ?? "#"}
                    target={field.helpLink.href ? "_blank" : undefined}
                    rel={field.helpLink.href ? "noreferrer" : undefined}
                    className="tw:rounded-xs tw:text-primary tw:underline tw:underline-offset-2 tw:outline-none tw:hover:text-primary/80 tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50"
                  >
                    {field.helpLink.label}
                  </a>
                ) : null}
              </p>
            ) : null}
          </div>
        )
      )}
    </>
  )
}

/* ───────────────────────────────────────────────────────────── component ── */

export default function ConnectSocial({
  className,
  children,
  screen = "network",
  assetBaseUrl = "/images",
  network = "facebook",
  title,
  subtitle,
  primaryNetworks = PRIMARY_NETWORKS,
  moreNetworks = MORE_NETWORKS,
  moreOpen = true,
  moreLabel = "More destinations",
  accounts = DEFAULT_ACCOUNTS,
  showSearch = true,
  searchPlaceholder,
  searchOpen,
  query,
  showConnectNew = true,
  loadingAccounts,
  emptyMessage,
  connectNewLabel,
  fields,
  error,
  backLabel = "Back",
  primaryLabel,
  primaryDisabled,
  loading,
  backVariant = "outline",
  footerStatus,
  showFooter,
  showBack,
  showPrimary,
  onClose,
  onBack,
  onPrimary,
  onSelectNetwork,
  onToggleMore,
  onSelectAccount,
  onConnectNew,
  onSearchChange,
  onSearchToggle,
  onFieldChange,
}: ConnectSocialProps) {
  const base = assetBaseUrl.replace(/\/$/, "")
  const label = networkLabel(network)
  // Both form screens read the same `fields` prop, so the default has to follow
  // the screen rather than sit in the parameter list.
  const resolvedFields =
    fields ?? (screen === "output-feed" ? OUTPUT_FEED_FIELDS : BLUESKY_FIELDS)

  // Header, footer and placeholder copy all name the network, so they read as
  // one sentence per screen rather than three unrelated defaults.
  const resolvedTitle =
    title ??
    (screen === "network"
      ? "Connect social"
      : screen === "output-feed"
        ? "Output Feed settings"
        : `Connect ${label}`)
  const resolvedSubtitle =
    subtitle ??
    (screen === "network"
      ? "Pick a network to connect your profile"
      : screen === "accounts"
        ? // Shortened from the frame's "Choose a page to post to, or connect a
          // new one." — that string measures 310.5px of ink against the 295px a
          // 375pt phone leaves inside the card, so it wrapped there and nowhere
          // else. This one clears every supported width on one line.
          "Choose a page, or connect a new one."
        : undefined)
  const resolvedPrimaryLabel =
    primaryLabel ?? (screen === "accounts" ? "Update" : "Connect")

  return (
    /* Radix Tooltip.Root throws without a Provider ancestor, so the component
       carries its own rather than depending on prod having wrapped the app in
       one — that keeps the file drop-in. TooltipProvider renders no DOM, and
       nesting inside a root provider prod may already have is harmless. */
    <TooltipProvider>
      <div
        className={cn(
          "tw:mx-auto tw:flex tw:w-full tw:flex-col tw:items-start tw:rounded-xl tw:border tw:border-border tw:bg-card tw:p-6 tw:text-card-foreground tw:shadow-xs",
          SCREEN_WIDTH[screen],
          SCREEN_GAP[screen],
          className
        )}
      >
        <ConnectDialogHeader
          title={resolvedTitle}
          subtitle={resolvedSubtitle}
          onClose={onClose}
        />

        {children ?? (screen === "network" ? (
          <NetworkScreen
            base={base}
            primary={primaryNetworks}
            more={moreNetworks}
            moreOpen={moreOpen}
            moreLabel={moreLabel}
            onSelectNetwork={onSelectNetwork}
            onToggleMore={onToggleMore}
          />
        ) : screen === "accounts" ? (
          <AccountsScreen
            network={network}
            accounts={accounts}
            query={query}
            showSearch={showSearch}
            searchOpen={searchOpen ?? Boolean(query)}
            searchPlaceholder={
              searchPlaceholder ?? `Search connected ${label} socials`
            }
            showConnectNew={showConnectNew}
            connectNewLabel={connectNewLabel}
            loadingAccounts={loadingAccounts}
            emptyMessage={
              emptyMessage ??
              `No ${label} accounts connected yet — connect one to continue.`
            }
            onSelectAccount={onSelectAccount}
            onConnectNew={onConnectNew}
            onSearchChange={onSearchChange}
            onSearchToggle={onSearchToggle}
          />
        ) : (
          <FormScreen
            fields={resolvedFields}
            error={error}
            onFieldChange={onFieldChange}
            onSubmit={onPrimary}
          />
        ))}

        {/* The picker has no footer in the design: choosing a mark advances it.
            `showFooter` overrides that for prod, whose picker is a step inside a
            longer flow and needs its way back. */}
        {showFooter ?? screen !== "network" ? (
          <ConnectDialogFooter
            backLabel={backLabel}
            backVariant={backVariant}
            primaryLabel={resolvedPrimaryLabel}
            primaryDisabled={
              primaryDisabled ??
              (screen === "accounts"
                ? !accounts.some((a) => a.selected)
                : false)
            }
            loading={loading}
            status={footerStatus}
            showBack={showBack}
            showPrimary={showPrimary}
            onBack={onBack}
            onPrimary={onPrimary}
          />
        ) : null}
      </div>
    </TooltipProvider>
  )
}
